/** Shared audit helpers (category + neutral EN summary for DB/export). */

export type AuditCategory = "crm" | "images" | "admin" | "social" | "auth" | "other";

export function auditActionCategory(action: string): AuditCategory {
  const a = (action || "").toLowerCase();
  if (a.startsWith("crm.")) return "crm";
  if (a.startsWith("images.")) return "images";
  if (a.startsWith("social.")) return "social";
  if (
    a.startsWith("admin.") ||
    a === "settings_update" ||
    a === "user_delete" ||
    a === "user.account.deleted"
  ) {
    return "admin";
  }
  if (a.startsWith("auth.")) return "auth";
  return "other";
}

export function shortenAuditId(id: string | null | undefined, headLen = 8): string {
  if (id == null || id === "") return "";
  const s = String(id);
  if (s.length <= headLen) return s;
  return `${s.slice(0, headLen)}…`;
}

export type AuditSummaryContext = {
  meta?: Record<string, unknown> | null;
  changed?: Array<{ field: string; from?: unknown; to?: unknown }> | null;
  entityType?: string | null;
  entityId?: string | null;
};

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return String(v);
}

/** Single-language summary for audit_log.summary and CSV export (English). */
export function buildAuditSummaryEn(action: string, ctx: AuditSummaryContext): string {
  const meta =
    ctx.meta && typeof ctx.meta === "object" && !Array.isArray(ctx.meta)
      ? (ctx.meta as Record<string, unknown>)
      : {};
  const dealTitle = str(meta.dealTitle).trim();
  const dealIdRaw = str(meta.dealId).trim();
  const dealHead = dealTitle || (dealIdRaw ? `deal ${shortenAuditId(dealIdRaw)}` : "");
  const et = (ctx.entityType || "").toLowerCase();
  const eid = ctx.entityId || "";

  switch (action) {
    case "crm.deal.stage_changed": {
      const title = dealTitle || (eid ? `deal ${shortenAuditId(eid)}` : "Deal");
      const fromS = str(meta.fromStage);
      const toS = str(meta.toStage);
      return `${title}: ${fromS || "?"} → ${toS || "?"}`;
    }
    case "crm.deal.updated": {
      const title = dealTitle || (eid ? `deal ${shortenAuditId(eid)}` : "Deal");
      const ch = ctx.changed;
      if (Array.isArray(ch) && ch.length > 0) {
        const parts = ch
          .slice(0, 2)
          .map((c) => `${c.field}: ${shortenAuditId(str(c.from), 12)}→${shortenAuditId(str(c.to), 12)}`);
        return `${title} · ${parts.join("; ")}${ch.length > 2 ? "…" : ""}`;
      }
      return `${title} updated`;
    }
    case "crm.file.uploaded": {
      const fn = str(meta.fileName);
      const head = dealHead || (eid ? shortenAuditId(eid) : "file");
      return fn ? `${head} · ${fn}` : `${head} · file`;
    }
    case "crm.contact.created":
      return str(meta.name) ? `Contact: ${str(meta.name)}` : "Contact created";
    case "crm.contact.updated":
      return eid ? `Contact ${shortenAuditId(eid)} updated` : "Contact updated";
    case "crm.contact.deleted": {
      const nm = str(meta.name).trim();
      return nm ? `Contact deleted: ${nm}` : eid ? `Contact ${shortenAuditId(eid)} deleted` : "Contact deleted";
    }
    case "crm.deal.created": {
      const ttl = dealTitle || str(meta.title).trim();
      return ttl || (eid ? `Deal ${shortenAuditId(eid)}` : "Deal created");
    }
    case "crm.deal.batch_deleted":
    case "crm.deal.batch_moved":
    case "crm.deal.batch_owner_changed":
      return `Batch: ${str(meta.success)} ok, ${str(meta.failed)} failed (${str(meta.requestedCount)} req)`;
    case "crm.calling.tel_click": {
      const title = dealTitle || (eid ? `deal ${shortenAuditId(eid)}` : "Deal");
      const surf = str(meta.surface).toLowerCase();
      const surfLabel = surf === "table" ? "table" : "mobile";
      const whom = str(meta.contactName).trim();
      const whomShort = whom.length > 80 ? `${whom.slice(0, 80)}…` : whom;
      return whomShort
        ? `${title} · call to ${whomShort} (${surfLabel})`
        : `${title} · call click (${surfLabel})`;
    }
    case "crm.note.created":
    case "crm.note.updated":
    case "crm.note.deleted":
      return dealHead
        ? `${dealHead} · note ${shortenAuditId(eid)}`
        : eid
          ? `Note ${shortenAuditId(eid)}`
          : action.replace(/^crm\.note\./, "Note ");
    case "crm.document.linked":
      return dealHead
        ? `${dealHead} · SF doc ${shortenAuditId(str(meta.sfDocumentId))}`
        : `Document linked ${shortenAuditId(eid)}`;
    case "crm.document.unlinked":
      return dealHead
        ? `${dealHead} · unlink ${shortenAuditId(eid)}`
        : `Document unlinked ${shortenAuditId(eid)}`;
    case "crm.deal_company.linked":
    case "crm.deal_company.unlinked": {
      const cmp =
        str(meta.companyName) ||
        (str(meta.ico) ? `IČO ${str(meta.ico)}` : "") ||
        shortenAuditId(str(meta.contactId));
      return dealHead ? `${dealHead} · company ${cmp}` : `Company ${cmp}`;
    }
    case "social.asset.approved":
    case "social.asset.generated": {
      const variant = str(meta.variant);
      return variant ? `${variant} · ${shortenAuditId(str(meta.sourcePhotoId))}` : action;
    }
    case "social.post.published":
      return str(meta.provider)
        ? `${str(meta.provider)} · ${shortenAuditId(eid)}`
        : shortenAuditId(eid);
    case "social.integration.meta.updated":
      return "Integration meta updated";
    case "images.folder.created":
    case "images.folder.updated":
    case "images.folder.deleted":
      return str(meta.folderName) || str(meta.folderId)
        ? `Folder ${str(meta.folderName) || shortenAuditId(str(meta.folderId))}`
        : action;
    case "images.photo.uploaded":
    case "images.photo.reordered":
    case "images.photo.moved":
    case "images.photo.deleted":
    case "images.photo.batch_deleted":
      return str(meta.folderId) ? `folder ${shortenAuditId(str(meta.folderId))}` : action;
    case "images.import.drive_started":
      return str(meta.folderId) ? `import → folder ${shortenAuditId(str(meta.folderId))}` : "Drive import started";
    case "admin.user.invited":
    case "admin.user.updated":
      return str(meta.email) || str(meta.invitedEmail) || str(meta.targetEmail) || shortenAuditId(eid);
    case "user_delete":
      return str(meta.email) || str(meta.deletedEmail) || `user ${shortenAuditId(eid)}`;
    case "user.account.deleted":
      return "Account deleted (self)";
    case "settings_update": {
      const ch = ctx.changed;
      if (Array.isArray(ch) && ch.length > 0) {
        const names = ch.map((c) => c.field).slice(0, 14);
        return names.length ? `Settings: ${names.join(", ")}${ch.length > 14 ? "…" : ""}` : "Settings updated";
      }
      const tried = ctx.meta && Array.isArray((ctx.meta as Record<string, unknown>).patchKeysTried)
        ? (ctx.meta as { patchKeysTried: string[] }).patchKeysTried
        : [];
      if (tried.length) return `Settings save (${tried.length} fields, no value change)`;
      return "Settings updated";
    }
    case "auth.password_changed":
      return "Password changed";
    case "auth.sessions_revoked_others":
      return "Other sessions revoked";
    case "generate_advance":
      return dealHead ? `${dealHead} · advance invoice` : "Advance invoice";
    default:
      if (et && eid) return `${et} ${shortenAuditId(eid)}`;
      if (eid) return shortenAuditId(eid);
      return action;
  }
}
